
import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useEmergencyUpdateQueue = (routineId: string) => {
  const { toast } = useToast();
  const pendingUpdates = useRef<Map<string, any>>(new Map());
  const isProcessing = useRef(false);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const queueUpdate = useCallback(async (exerciseId: string, field: string, value: any) => {
    const updateKey = `${exerciseId}-${field}`;
    
    // Almacenar localmente de inmediato
    pendingUpdates.current.set(updateKey, { 
      exerciseId, 
      field, 
      value, 
      timestamp: Date.now(),
      priority: getFieldPriority(field)
    });
    
    console.log(`🚨 EMERGENCY QUEUE: Encolado ${updateKey}:`, value);

    // Procesar solo si no hay otra operación en curso
    if (!isProcessing.current) {
      isProcessing.current = true;
      
      // Cancelar timeout anterior
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      
      // Procesar con delay mínimo para agrupar actualizaciones
      processingTimeoutRef.current = setTimeout(async () => {
        await processQueue();
      }, 500); // 500ms para agrupar múltiples cambios
    }

    return Promise.resolve(); // Resolver inmediatamente
  }, []);

  // Función para determinar prioridad de campos
  const getFieldPriority = useCallback((field: string): number => {
    switch (field) {
      case 'sets':
        return 1; // Máxima prioridad
      case 'set_rest_time':
        return 2; // Alta prioridad
      case 'exercise_rest_time':
        return 3; // Prioridad media
      default:
        return 4; // Prioridad baja
    }
  }, []);

  // Procesar cola de actualizaciones
  const processQueue = useCallback(async () => {
    if (pendingUpdates.current.size === 0) {
      isProcessing.current = false;
      return;
    }

    console.log(`🔄 EMERGENCY QUEUE: Procesando ${pendingUpdates.current.size} actualizaciones pendientes`);

    // Convertir a array y ordenar por prioridad
    const updates = Array.from(pendingUpdates.current.entries())
      .sort(([, a], [, b]) => a.priority - b.priority);

    // Procesar actualizaciones en lotes por ejercicio
    const exerciseGroups = new Map<string, Array<any>>();
    
    updates.forEach(([key, update]) => {
      const exerciseId = update.exerciseId;
      if (!exerciseGroups.has(exerciseId)) {
        exerciseGroups.set(exerciseId, []);
      }
      exerciseGroups.get(exerciseId)!.push(update);
    });

    // Procesar cada ejercicio por separado
    for (const [exerciseId, exerciseUpdates] of exerciseGroups) {
      try {
        // Agrupar actualizaciones del mismo ejercicio en una sola query
        const updateData: any = {};
        exerciseUpdates.forEach(update => {
          updateData[update.field] = update.value;
        });

        console.log(`📤 EMERGENCY QUEUE: Actualizando ejercicio ${exerciseId}:`, updateData);

        const { error } = await supabase
          .from("exercises")
          .update(updateData)
          .eq("id", exerciseId);

        if (error) {
          console.error(`❌ EMERGENCY QUEUE: Error actualizando ${exerciseId}:`, error);
          
          // Solo mostrar toast para errores críticos
          toast({
            variant: "destructive",
            title: "Error de emergencia",
            description: `No se pudieron guardar algunos cambios. Se mantendrán localmente.`,
            duration: 3000
          });
        } else {
          console.log(`✅ EMERGENCY QUEUE: Ejercicio ${exerciseId} actualizado exitosamente`);
          
          // Eliminar actualizaciones procesadas exitosamente
          exerciseUpdates.forEach(update => {
            const key = `${update.exerciseId}-${update.field}`;
            pendingUpdates.current.delete(key);
          });
        }
        
        // Pequeña pausa entre ejercicios para no sobrecargar
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error: any) {
        console.error(`💥 EMERGENCY QUEUE: Excepción procesando ${exerciseId}:`, error);
      }
    }

    // Verificar si quedan actualizaciones pendientes
    if (pendingUpdates.current.size > 0) {
      console.log(`⚠️ EMERGENCY QUEUE: ${pendingUpdates.current.size} actualizaciones fallaron, reintentando en 5s`);
      
      // Reintentar en 5 segundos
      setTimeout(() => {
        if (pendingUpdates.current.size > 0) {
          processQueue();
        } else {
          isProcessing.current = false;
        }
      }, 5000);
    } else {
      console.log(`✅ EMERGENCY QUEUE: Todas las actualizaciones procesadas`);
      isProcessing.current = false;
    }
  }, [toast]);

  const getPendingValue = useCallback((exerciseId: string, field: string) => {
    const updateKey = `${exerciseId}-${field}`;
    return pendingUpdates.current.get(updateKey)?.value;
  }, []);

  const getPendingCount = useCallback(() => {
    return pendingUpdates.current.size;
  }, []);

  const clearQueue = useCallback(() => {
    pendingUpdates.current.clear();
    isProcessing.current = false;
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }
  }, []);

  return {
    queueUpdate,
    getPendingValue,
    getPendingCount,
    clearQueue
  };
};
